"use client";

import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";

interface Section {
    id: string;
    name: string;
}

const sections: Section[] = [
    { id: "hero", name: "Hero" },
    { id: "projects", name: "Projects" },
    { id: "skills", name: "Skills" },
    { id: "case-studies", name: "Designs" },
    { id: "contact", name: "Contact" },
    { id: "footer", name: "Footer" },
];

export default function ScrollIndicator() {
    const [activeSection, setActiveSection] = useState<string>("hero");
    // Removed isDarkMode state and observer as we now use CSS semantic tokens

    const handleScroll = useCallback(() => {
        // Special check for bottom of page to force activate footer
        if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 50) {
            setActiveSection("footer");
            return;
        }

        const scrollPosition = window.scrollY + window.innerHeight / 2;

        for (const section of sections) {
            const element = document.getElementById(section.id);
            if (element) {
                const { offsetTop, offsetHeight } = element;
                if (
                    scrollPosition >= offsetTop &&
                    scrollPosition < offsetTop + offsetHeight
                ) {
                    setActiveSection(section.id);
                    break;
                }
            }
        }
    }, []);

    useEffect(() => {
        window.addEventListener("scroll", handleScroll, { passive: true });
        handleScroll(); // Initial check

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [handleScroll]);

    const [visibleTooltip, setVisibleTooltip] = useState<string | null>(null);

    useEffect(() => {
        setVisibleTooltip(activeSection);
        const timer = setTimeout(() => {
            setVisibleTooltip(null);
        }, 2000);

        return () => clearTimeout(timer);
    }, [activeSection]);

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: "smooth" });
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent, sectionId: string) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            scrollToSection(sectionId);
        }
    };

    return (
        <nav
            aria-label="Section navigation"
            className="fixed left-6 top-1/2 -translate-y-1/2 z-40 flex flex-col gap-3 md:left-auto md:right-8 md:gap-4"
        >
            {sections.map((section) => {
                const isActive = activeSection === section.id;
                const isTooltipVisible = visibleTooltip === section.id;

                return (
                    <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        onKeyDown={(e) => handleKeyDown(e, section.id)}
                        aria-label={`Navigate to ${section.name}`}
                        aria-current={isActive ? "true" : undefined}
                        className="group relative w-3.5 h-3.5 md:w-4 md:h-4 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background cursor-pointer"
                    >
                        {/* Tooltip - left side on mobile, right side on desktop */}
                        <span
                            className={`absolute top-1/2 -translate-y-1/2 px-2 py-1 text-[10px] md:px-3 md:py-1.5 md:text-xs font-medium rounded-md pointer-events-none transition-all duration-300 whitespace-nowrap shadow-lg
                                left-full ml-4 md:left-auto md:right-full md:ml-0 md:mr-4
                                ${isTooltipVisible
                                    ? "opacity-100 translate-x-0"
                                    : "opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 md:translate-x-2 md:group-hover:translate-x-0"
                                }
                                bg-foreground text-background`
                            }
                        >
                            {section.name}

                        </span>

                        {/* Diamond shape (inactive) */}
                        <motion.div
                            className="absolute w-2 h-2 md:w-2.5 md:h-2.5 bg-accent"
                            initial={false}
                            animate={{
                                rotate: isActive ? 0 : 45,
                                scale: isActive ? 0 : 1,
                                opacity: isActive ? 0 : 1,
                            }}
                            transition={{
                                duration: 0.3,
                                ease: [0.4, 0, 0.2, 1],
                            }}
                        />

                        {/* Square outline (active) */}
                        <motion.div
                            className="absolute w-2.5 h-2.5 md:w-3 md:h-3 border-2 border-accent bg-transparent"
                            initial={false}
                            animate={{
                                scale: isActive ? 1 : 0,
                                opacity: isActive ? 1 : 0,
                            }}
                            transition={{
                                duration: 0.3,
                                ease: [0.4, 0, 0.2, 1],
                            }}
                        />
                    </button>
                );
            })}
        </nav>
    );
}
